import React, { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Pagination,
  Table,
  Modal,
  Spinner,
  Alert,
} from "flowbite-react";
import { FaArrowsLeftRight, FaRegEye } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery, useQueryClient } from "react-query";
import { ADMIN, CATEGORIES } from "../../config/apiConfig";
import { IoMdAdd } from "react-icons/io";
import { FaRegEdit } from "react-icons/fa";
import EmptyTable from "./EmptyTable";
import TableLoading from "../TableLoading";
import TableQueriesForm from "../forms/TableQueriesForm";
import { useApplication } from "../../contexts/ApplicationContext";
import { formatToReal } from "../../utils/utils";
import { Tooltip } from "react-tooltip";

const ITEMS_PER_PAGE = 20;

const AdminTablesTable = ({ isActive = true }) => {
  const queryClient = useQueryClient();
  const { user } = useApplication();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [isDrawerTableQueries, setIsDrawerTableQueries] = useState(false);
  const [type, setType] = useState("CREATE");
  const [selectedTable, setSelectedTable] = useState(null);

  // --- NOVO: modal de empresas por tabela
  const [isCompaniesModalOpen, setIsCompaniesModalOpen] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [companiesError, setCompaniesError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedTableForCompanies, setSelectedTableForCompanies] =
    useState(null);

  const {
    isLoading: isLoadingTables,
    error: errorListTables,
    data: listTables,
  } = useQuery(
    ["listTables", currentPage, nameParam],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_TABLES}?page=${currentPage}&search=${nameParam}&itemsPerPage=${ITEMS_PER_PAGE}`
      );
      setTotalPages(data.data.totalPages || 1);
      setTotalCount(data.data.totalCount || 0);
      return data.data.data;
    },
    { enabled: isActive }
  );

  const { isLoading: isLoadingCategories, data: categories } = useQuery(
    ["categories"],
    async () => {
      const { data } = await axios.get(
        `${CATEGORIES.GET_ALL}?itemsPerPage=${-1}`
      );
      return data.data.data;
    },
    { enabled: isActive }
  );

  const handleEditTableQueries = (table, type) => {
    setType(type);
    setSelectedTable(table);
    setIsDrawerTableQueries(true);
  };

  // --- NOVO: abrir modal e carregar clientes
  const handleViewCompanies = async (table) => {
    setSelectedTableForCompanies(table);
    setIsCompaniesModalOpen(true);
    setCompanies([]);
    setCompaniesError(null);
    setCompaniesLoading(true);
    try {
      // Ajuste a rota se necessário:
      // opção A (recomendada): endpoint dedicado
      // const { data } = await axios.get(`${ADMIN.GET_TABLES}/${table.id}/companies?itemsPerPage=-1`);
      // opção B (genérica - usando filtro por tableId):
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANIES_BY_TABLE}/${table.id}?itemsPerPage=-1`
      );
      const list = data?.data?.data ?? data?.data ?? data?.companies ?? [];
      setCompanies(Array.isArray(list) ? list : []);
    } catch (err) {
      setCompaniesError("Não foi possível carregar os clientes vinculados.");
      setCompanies([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const headerSubtitle = useMemo(() => {
    if (isLoadingTables) return "Carregando tabelas…";
    if (!listTables?.length) return "Nenhuma tabela encontrada";
    return `${totalCount} ${totalCount === 1 ? "registro" : "registros"}`;
  }, [isLoadingTables, listTables, totalCount]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="relative flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg md:text-xl font-semibold text-gray-900">
            Tabelas de consultas
          </h2>
          <p className="text-xs text-gray-500">{headerSubtitle}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-primary focus:ring-4 focus:ring-indigo-200"
            onClick={() => handleEditTableQueries(null, "CREATE")}
          >
            <div className="flex items-center gap-2">
              <IoMdAdd className="text-base" />
              <span className="hidden md:inline">Criar tabela</span>
              <span className="md:hidden">Nova</span>
            </div>
          </Button>
        </div>
      </div>

      {/* Hint mobile */}
      {((listTables?.length ?? 0) > 0 || isLoadingTables) && (
        <small
          className="gap-2 items-center mt-1 -mb-1 flex md:hidden justify-center text-gray-600"
          style={{ fontSize: 10 }}
        >
          <span>Deslize para o lado para ver tudo</span>
          <FaArrowsLeftRight />
        </small>
      )}

      {/* Table Card */}
      {listTables?.length > 0 || isLoadingTables ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
          <div className="overflow-x-auto">
            <Table hoverable={true} striped className="text-xs xl:text-sm">
              <Table.Head className="sticky top-0 bg-gray-100">
                <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                  Tabela
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                  VCM
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                  Qtd. clientes
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                  Status
                </Table.HeadCell>
                <Table.HeadCell className="text-center bg-gray-100 text-gray-700">
                  Ações
                </Table.HeadCell>
              </Table.Head>

              <Table.Body className="divide-y">
                {!isLoadingTables ? (
                  listTables?.length ? (
                    listTables.map((table, index) => {
                      const statusActive = Number(table.isActive) === 1;
                      const tipEdit = `tooltip-edit-${table?.id ?? index}`;
                      const tipView = `tooltip-view-${table?.id ?? index}`;
                      return (
                        <Table.Row
                          key={table.id ?? index}
                          className="bg-white focus-within:bg-indigo-50/40 hover:bg-indigo-50/40"
                        >
                          <Table.Cell className="font-medium text-gray-900 text-center py-2">
                            <span className="inline-flex items-center gap-2">
                              <span
                                className="truncate max-w-[220px] md:max-w-[320px]"
                                title={table.name}
                              >
                                {table.name}
                              </span>
                            </span>
                          </Table.Cell>

                          <Table.Cell className="text-center py-2">
                            {table.vcm ? (
                              <span className="font-medium text-gray-800">
                                {formatToReal(table.vcm)}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </Table.Cell>

                          <Table.Cell className="text-center py-2">
                            <span className="inline-flex items-center justify-center min-w-[3ch] rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
                              {table?.companiesCount || 0}
                            </span>
                          </Table.Cell>

                          <Table.Cell className="text-center py-2">
                            {statusActive ? (
                              <Badge color="success" className="mx-auto w-fit">
                                Ativo
                              </Badge>
                            ) : (
                              <Badge color="failure" className="mx-auto w-fit">
                                Inativo
                              </Badge>
                            )}
                          </Table.Cell>

                          <Table.Cell className="text-center py-2">
                            <div className="flex gap-3 justify-center items-center">
                              {/* Visualizar clientes */}
                              <Tooltip
                                id={tipView}
                                content="Visualizar clientes"
                              />
                              <FaRegEye
                                data-tooltip-id={tipView}
                                fontSize="16"
                                className="text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                                onClick={() => handleViewCompanies(table)}
                                aria-label={`Visualizar clientes de ${
                                  table?.name ?? "tabela"
                                }`}
                              />

                              {/* Editar tabela */}
                              <Tooltip id={tipEdit} content="Editar tabela" />
                              <FaRegEdit
                                data-tooltip-id={tipEdit}
                                fontSize="16"
                                className="text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
                                onClick={() =>
                                  handleEditTableQueries(table, "EDIT")
                                }
                                aria-label={`Editar ${table?.name ?? "tabela"}`}
                              />
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  ) : null
                ) : (
                  <TableLoading cols={5} />
                )}
              </Table.Body>
            </Table>
          </div>

          {/* Footer info + pagination */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 border-t border-gray-100 bg-gray-50">
            {totalCount > 0 ? (
              <div className="text-sm text-gray-600 text-center md:text-left">
                Exibindo {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} de{" "}
                {totalCount} registros
              </div>
            ) : (
              <div />
            )}

            {totalPages > 1 && (
              <div className="flex overflow-x-auto justify-center md:justify-end">
                <Pagination
                  layout="pagination"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  previousLabel=""
                  nextLabel=""
                  showIcons
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyTable icon="search_off">Nenhuma tabela encontrada</EmptyTable>
      )}

      {/* Drawer de criar/editar tabela */}
      <Modal
        show={isDrawerTableQueries}
        onClose={() => setIsDrawerTableQueries(false)}
        size="4xl"
        dismissible
        className="!z-[60]"
      >
        <Modal.Header className="py-2 flex items-center">
          <div className="text-sm md:text-base">
            {type === "CREATE" ? "Criar" : "Editar"} Tabela de consultas
          </div>
        </Modal.Header>
        <Modal.Body className="pt-2">
          <TableQueriesForm
            selectedTable={selectedTable}
            categories={categories}
            onClose={setIsDrawerTableQueries}
            fetchTables={() => {
              queryClient.invalidateQueries(["listTables"], { exact: false });
              queryClient.refetchQueries(["listTables"], {
                active: true,
                inactive: true,
              });
            }}
            type={type}
          />
        </Modal.Body>
      </Modal>

      {/* NOVO: Modal de empresas vinculadas */}
      <Modal
        show={isCompaniesModalOpen}
        onClose={() => setIsCompaniesModalOpen(false)}
        size="4xl"
        dismissible
      >
        <Modal.Header className="py-2 flex items-center text-base">
          <span className="text-base">
            Clientes vinculados{" "}
            {selectedTableForCompanies
              ? `— ${selectedTableForCompanies.name}`
              : ""}
          </span>
        </Modal.Header>
        <Modal.Body>
          {companiesLoading ? (
            <div className="flex items-center justify-center py-10">
              <Spinner />
            </div>
          ) : companiesError ? (
            <Alert color="failure">{companiesError}</Alert>
          ) : companies.length === 0 ? (
            <div className="text-center text-sm text-gray-500 py-6">
              Nenhum cliente vinculado a esta tabela.
            </div>
          ) : (
            <>
              <div className="text-xs text-gray-600 mb-2 flex justify-end">
                {companies.length}{" "}
                {companies.length === 1 ? "cliente" : "clientes"}
              </div>
              <div className="max-h-[60vh] overflow-auto rounded border">
                <Table hoverable striped className="text-xs xl:text-sm">
                  <Table.Head>
                    <Table.HeadCell>Nome / Razão Social</Table.HeadCell>
                    <Table.HeadCell className="text-center">
                      Status
                    </Table.HeadCell>
                  </Table.Head>
                  <Table.Body className="divide-y">
                    {companies.map((c) => (
                      <Table.Row key={c.id}>
                        <Table.Cell className="font-medium text-gray-900">
                          {c.name || c.socialReason || "—"}
                        </Table.Cell>
                        <Table.Cell className="text-center">
                          {Number(c.status) === 1 ? (
                            <Badge color="success" className="w-fit mx-auto">
                              Ativo
                            </Badge>
                          ) : Number(c.status) === 0 ? (
                            <Badge color="warning" className="w-fit mx-auto">
                              Bloqueado
                            </Badge>
                          ) : (
                            <Badge color="failure" className="w-fit mx-auto">
                              Inadimplente
                            </Badge>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="pt-2 pb-3 flex justify-center">
          <Button color="light" onClick={() => setIsCompaniesModalOpen(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminTablesTable;
